import { inject, Injectable } from '@angular/core'
import { map, mergeMap, scan, shareReplay, startWith, tap } from 'rxjs/operators'
import {
  InboundPrivateMessageQuery,
  OutboundPrivateMessageQuery,
  PrivateMessageQuery,
} from '../../../services/domain/privateMessages/PrivateMessageQuery'
import { PrivateMessage, PrivateMessageQueryType } from '../../../models/privateMessage'
import { firstValueFrom, ReplaySubject } from 'rxjs'
import { PrivateMessagesService } from '../../../services/domain/privateMessages/private-messages.service'
import { RoleAssistantAssistanceEditorComponent } from '../../../features/roleAssistant/role-assistant-assistance-editor/role-assistant-assistance-editor.component'
import { User } from '../../../models/user'
import { AssistanceService } from '../../../services/domain/assistance/assistance.service'
import { AssistantService } from '../../../services/domain/assistant/assistant.service'
import { ModalController } from '@ionic/angular'

@Injectable({
  providedIn: 'root',
})
export class MessagingPageService {
  private privMsgService = inject(PrivateMessagesService)
  private assistanceService = inject(AssistanceService)
  private assistantService = inject(AssistantService)
  private modalCtrl = inject(ModalController)

  private inboundPrivateMessagesWithOpts$$ = new ReplaySubject<InboundPrivateMessageQuery>(1)
  private inboundPrivateMessagesWithOpts$ = this.inboundPrivateMessagesWithOpts$$.asObservable().pipe(
    startWith({
      type: PrivateMessageQueryType.feedback,
      offset: 0,
      onlyUnread: false,
    }),
    tap((value) => console.log('INBOUND OPTS', value)),
    scan((acc, current) => {
      return Object.assign(acc, current)
    }, {} as InboundPrivateMessageQuery),
    tap((opts) => console.log('INBOUND OPTS RESULT', opts)),
    shareReplay({ bufferSize: 1, refCount: false }),
  )
  inboundPrivateMessages$ = this.inboundPrivateMessagesWithOpts$.pipe(
    mergeMap((opts) =>
      this.privMsgService.getInboundForCurrentUserPaginated(20, opts).pipe(map((p) => ({ data: p, opts }))),
    ),
    scan(
      (acc: PrivateMessage[], newValue: { data: PrivateMessage[]; opts: PrivateMessageQuery }) =>
        this.upsert(acc, newValue.data, newValue.opts),
      [],
    ),
  )
  inboundMsgType$ = this.inboundPrivateMessagesWithOpts$.pipe(map((opts) => opts.type))

  private outboundPrivateMessages$WithOffset$$ = new ReplaySubject<OutboundPrivateMessageQuery>(1)
  private outboundPrivateMessagesWithOffset$ = this.outboundPrivateMessages$WithOffset$$.asObservable().pipe(
    startWith({
      type: PrivateMessageQueryType.direct,
      offset: 0,
      onlyUnread: false,
    }),
    scan((acc, current) => {
      return Object.assign(acc, current)
    }, {} as OutboundPrivateMessageQuery),
    shareReplay({ bufferSize: 1, refCount: false }),
  )
  outboundPrivateMessages$ = this.outboundPrivateMessagesWithOffset$.pipe(
    mergeMap((opts) =>
      this.privMsgService.getOutboundForCurrentUserPaginated(20, opts).pipe(map((p) => ({ data: p, opts }))),
    ),
    scan(
      (acc: PrivateMessage[], newValue: { data: PrivateMessage[]; opts: PrivateMessageQuery }) =>
        this.upsert(acc, newValue.data, newValue.opts),
      [],
    ),
  )
  outboundMsgType$ = this.outboundPrivateMessagesWithOffset$.pipe(map((opts) => opts.type))

  // remove from acc anything that has the same id in newValue. On offset 0 resets the upsert.
  private upsert(acc: PrivateMessage[], newValue: PrivateMessage[], opts?: PrivateMessageQuery) {
    if (opts?.offset === 0) return newValue

    const newIds = newValue.map((i) => i.id)
    const filteredAcc = acc.filter((item) => !newIds.includes(item.id))
    return [...filteredAcc, ...newValue]
  }

  inboundPrivateMessagesLoadOffset(offset: number) {
    this.inboundPrivateMessagesWithOpts$$.next({ offset })
  }
  outboundPrivateMessagesLoadOffset(offset: number) {
    this.outboundPrivateMessages$WithOffset$$.next({ offset })
  }

  setPrivateMessageType(type: PrivateMessageQueryType) {
    this.inboundPrivateMessagesWithOpts$$.next({ type, offset: 0 })
  }
  setOutboundPrivateMessageType(type: PrivateMessageQueryType) {
    this.outboundPrivateMessages$WithOffset$$.next({ type, offset: 0 })
  }
  setAssistantFilter(assistantId: number) {
    this.inboundPrivateMessagesWithOpts$$.next({
      concernedAssistantId: assistantId,
      offset: 0,
    })
  }
  setClientFilter(clientId: number) {
    this.inboundPrivateMessagesWithOpts$$.next({
      concernedClientId: clientId,
      offset: 0,
    })
  }
  setSenderFilter(userId: number) {
    this.inboundPrivateMessagesWithOpts$$.next({
      senderUserId: userId,
      offset: 0,
    })
  }
  setOnlyUnreadFilter(onlyUnread: boolean) {
    this.inboundPrivateMessagesWithOpts$$.next({ onlyUnread, offset: 0 })
  }

  async createRoleAssistantAssistanceEditorModal(assistanceId: number, currentUser: User) {
    const assistance = await firstValueFrom(this.assistanceService.getById(assistanceId))
    const assistantId = await firstValueFrom(this.assistantService.getAssistantIdByUserId(currentUser.id))

    console.log('createRoleAssistantAssistanceEditorModal', assistance, assistantId)

    return await this.modalCtrl.create({
      component: RoleAssistantAssistanceEditorComponent,
      componentProps: { assistance, assistantId },
      cssClass: 'roleAssistantAssistanceEditorModal',
      backdropDismiss: false,
    })
  }
}
